import { useCallback, useMemo, useRef, useState } from "react"
import type { FormEvent } from "react"
import fp from "lodash/fp"
import { Button, Tooltip, useTheme } from "@mui/material"
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded"
import ArrowDropUpSharpIcon from "@mui/icons-material/ArrowDropUpSharp"
import ArrowDropDownSharpIcon from "@mui/icons-material/ArrowDropDownSharp"
import { useTranslation } from "react-i18next"

import type { EntityType } from "types/common.types"
import type { CsvDateRange } from "types/csv.types"
import useSelectOptions from "helpers/hooks/useSelectOptions"
import DateRangeTooltipContent from "widgets/dates/DateRangeTooltipContent"
import DateRangeTriggerLabel from "widgets/dates/DateRangeTriggerLabel"
import CsvDatesPopover from "./CsvDatesPopover"

interface CsvDatePickerProps {
  value: CsvDateRange
  onChange: (value: CsvDateRange) => void
  entity: EntityType
  maxRangeMonths: number
}

const CsvDatePicker = ({
  value,
  onChange,
  entity,
  maxRangeMonths,
}: CsvDatePickerProps) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { getSelectOptionsByEntity } = useSelectOptions()
  const selectOptions = useMemo(
    () => getSelectOptionsByEntity(entity),
    [entity, getSelectOptionsByEntity],
  )
  const [isOpen, setIsOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement | null>(null)

  const onClose = useCallback(() => setIsOpen(false), [])
  const onOpen = useCallback((event: FormEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    event.preventDefault()
    setIsOpen((prev) => !prev)
  }, [])

  const selectedOption = useMemo(
    () => fp.find({ value: value.range }, selectOptions),
    [value.range, selectOptions],
  )

  return (
    <>
      <Tooltip
        title={
          <DateRangeTooltipContent
            fromDate={value.fromDate}
            toDate={value.toDate}
            header={value.zoom ? t("dates.ZOOMED_PERIOD") : undefined}
          />
        }
        enterTouchDelay={0}
        arrow
        PopperProps={{ style: { zIndex: 1298 } }} // Avoids having tooltip on top
      >
        <Button
          aria-label="csv dates select button"
          variant="outlined"
          onClick={onOpen}
          startIcon={<AccessTimeRoundedIcon />}
          endIcon={isOpen ? <ArrowDropUpSharpIcon /> : <ArrowDropDownSharpIcon />}
          ref={anchorRef}
          sx={{
            color: theme.palette.text.secondary,
            borderColor: theme.palette.neutral[400],
          }}
        >
          <DateRangeTriggerLabel
            option={selectedOption}
            zoom={value.zoom}
            fromDate={value.fromDate}
            toDate={value.toDate}
          />
        </Button>
      </Tooltip>
      {isOpen && (
        <CsvDatesPopover
          value={value}
          onChange={onChange}
          entity={entity}
          maxRangeMonths={maxRangeMonths}
          anchorEl={anchorRef.current}
          open={isOpen}
          handleClose={onClose}
        />
      )}
    </>
  )
}

export default CsvDatePicker
